/**
 * Psychedelics Legality Map — interactions.
 *
 * Everything here is an enhancement: the country list, tables and links all work
 * server-rendered, so this file only adds the map tooltip, map navigation, the
 * country picker shortcut, and the substance tab switching.
 */

const TOOLTIP_OFFSET = 5;
const HIDE_DELAY = 180;

/**
 * Map: one tooltip on hover/focus, navigation on click, keyboard, and tap.
 *
 * Desktop: the plaque sits 5px above the cursor and follows it. It remains
 * open while the cursor is on the country or the plaque, then closes after a
 * short delay. The whole plaque is the country-page link, not just the action
 * text. The hover-bridge under the plaque does not take clicks, so a click on
 * the country still reaches the map (scroll on Start Here, navigate elsewhere).
 *
 * Touch: a tap on a country opens and pins the plaque. Tapping the plaque
 * follows the link; tapping elsewhere closes it; tapping another country
 * updates the contents. On Start Here a tap also scrolls the matching
 * highlight card and does not leave the page.
 *
 * Every country answers the pointer, including the ones the dataset does not
 * cover yet — they say so instead of going nowhere silently.
 */
function initMap(map) {
    const tooltip = map.querySelector('.som-map__tooltip');
    const countries = map.querySelectorAll('.som-map__country--hoverable');

    if (!tooltip || !countries.length) {
        return;
    }

    const name = tooltip.querySelector('.som-map__tooltip-name');
    const flag = tooltip.querySelector('.som-map__tooltip-flag');
    const status = tooltip.querySelector('.som-map__tooltip-status');
    const action = tooltip.querySelector('.som-map__tooltip-action');
    const brief = map.hasAttribute('data-scroll-highlights');
    const highlightsRoot = map.closest('[data-legality-highlights]');
    let activeCountry = null;
    let pinned = false;
    let hideTimer = null;
    let usingTouch = false;

    const cancelHide = () => {
        if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
    };

    const setHot = (country) => {
        if (activeCountry && activeCountry !== country) {
            activeCountry.classList.remove('is-hot');
        }

        activeCountry = country || null;

        if (activeCountry) {
            activeCountry.classList.add('is-hot');
        }
    };

    const hide = () => {
        cancelHide();
        tooltip.hidden = true;
        tooltip.setAttribute('aria-hidden', 'true');
        tooltip.classList.remove('som-map__tooltip--pinned');
        tooltip.removeAttribute('href');
        setHot(null);
        pinned = false;
    };

    const requestHide = () => {
        if (pinned) {
            return;
        }

        cancelHide();
        hideTimer = setTimeout(() => {
            hideTimer = null;
            hide();
        }, HIDE_DELAY);
    };

    const pin = () => {
        pinned = true;
        cancelHide();
        tooltip.classList.add('som-map__tooltip--pinned');
    };

    const scrollHighlight = (country) => {
        if (!brief || !highlightsRoot) {
            return;
        }

        const slug = country.dataset.slug;
        if (!slug) {
            return;
        }

        const item = highlightsRoot.querySelector(`.som-highlights__item[data-slug="${slug}"]`);
        if (!item) {
            return;
        }

        highlightsRoot.querySelectorAll('.som-highlights__item.is-active').forEach((el) => {
            el.classList.remove('is-active');
        });
        item.classList.add('is-active');

        const list = item.closest('.som-highlights__list') || item.parentElement;
        if (!list) {
            return;
        }

        const listRect = list.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const padding = parseFloat(getComputedStyle(list).paddingInlineStart) || 0;
        const left = list.scrollLeft + itemRect.left - listRect.left - padding;

        list.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    };

    const fill = (country) => {
        const label = country.dataset.name || '';
        const flagSrc = country.dataset.flagSrc || '';
        const url = country.dataset.url || '';

        name.textContent = label;

        if (flag) {
            if (flagSrc) {
                flag.src = flagSrc;
                flag.hidden = false;
            } else {
                flag.removeAttribute('src');
                flag.hidden = true;
            }
        }

        if (status) {
            status.hidden = brief;
            status.textContent = brief ? '' : (country.dataset.statusLabel || '');
        }

        if (url) {
            tooltip.href = url;
        } else {
            tooltip.removeAttribute('href');
        }

        if (action) {
            action.hidden = !url;
        }
    };

    const place = (x, y) => {
        const bounds = map.getBoundingClientRect();
        tooltip.style.left = `${x - bounds.left}px`;
        tooltip.style.top = `${y - bounds.top - TOOLTIP_OFFSET}px`;
        tooltip.hidden = false;
        tooltip.setAttribute('aria-hidden', 'false');
    };

    const show = (country, x, y) => {
        cancelHide();
        setHot(country);
        fill(country);
        place(x, y);
    };

    const showCentred = (country) => {
        const box = country.getBoundingClientRect();
        show(country, box.left + box.width / 2, box.top);
    };

    const navigate = (country) => {
        const url = country.dataset.url;
        if (url) {
            window.location.assign(url);
        }
    };

    map.addEventListener('pointerdown', (event) => {
        usingTouch = event.pointerType === 'touch';
    });

    countries.forEach((country) => {
        country.addEventListener('mouseenter', (event) => {
            if (usingTouch) {
                return;
            }

            pinned = false;
            tooltip.classList.remove('som-map__tooltip--pinned');
            show(country, event.clientX, event.clientY);
        });
        country.addEventListener('mousemove', (event) => {
            if (usingTouch || pinned) {
                return;
            }

            show(country, event.clientX, event.clientY);
        });
        country.addEventListener('mouseleave', requestHide);

        country.addEventListener('focus', () => showCentred(country));
        country.addEventListener('blur', (event) => {
            if (tooltip.contains(event.relatedTarget)) {
                return;
            }

            requestHide();
        });

        country.addEventListener('click', (event) => {
            if (usingTouch) {
                event.preventDefault();
                showCentred(country);
                pin();

                if (brief) {
                    scrollHighlight(country);
                }

                return;
            }

            if (brief) {
                event.preventDefault();
                showCentred(country);
                scrollHighlight(country);
                return;
            }

            if (country.dataset.url) {
                navigate(country);
                return;
            }

            showCentred(country);
        });

        country.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }

            event.preventDefault();

            if (brief) {
                showCentred(country);
                scrollHighlight(country);
                return;
            }

            if (country.dataset.url) {
                navigate(country);
                return;
            }

            showCentred(country);
        });
    });

    tooltip.addEventListener('mouseenter', cancelHide);
    tooltip.addEventListener('mouseleave', requestHide);
    tooltip.addEventListener('focusin', cancelHide);
    tooltip.addEventListener('focusout', (event) => {
        if (tooltip.contains(event.relatedTarget)) {
            return;
        }

        if (activeCountry && activeCountry.contains(event.relatedTarget)) {
            return;
        }

        requestHide();
    });
    tooltip.addEventListener('click', (event) => {
        if (!tooltip.getAttribute('href')) {
            event.preventDefault();
        }
    });

    map.addEventListener('mouseleave', (event) => {
        if (tooltip.contains(event.relatedTarget)) {
            return;
        }

        requestHide();
    });

    document.addEventListener('pointerdown', (event) => {
        if (!pinned || tooltip.hidden) {
            return;
        }

        if (tooltip.contains(event.target)) {
            return;
        }

        if (map.contains(event.target) && event.target.closest('.som-map__country--hoverable')) {
            return;
        }

        hide();
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            hide();
        }
    });
}

/** Country picker: selecting an option navigates to that country. */
function initCountrySelect(select) {
    select.addEventListener('change', () => {
        if (select.value) {
            window.location.assign(select.value);
        }
    });
}

/** Substance tabs over the country list. All panels are already in the DOM. */
function initTabs(tablist) {
    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));

    if (tabs.length < 2) {
        return;
    }

    const select = (tab) => {
        tabs.forEach((other) => {
            const selected = other === tab;
            const panel = document.getElementById(other.getAttribute('aria-controls'));

            other.setAttribute('aria-selected', selected ? 'true' : 'false');
            other.setAttribute('tabindex', selected ? '0' : '-1');

            if (panel) {
                panel.hidden = !selected;
            }
        });
    };

    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));

        tab.addEventListener('keydown', (event) => {
            const step = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: tabs.length - 1 - index }[event.key];

            if (step === undefined) {
                return;
            }

            event.preventDefault();
            const next = tabs[(index + step + tabs.length) % tabs.length];
            select(next);
            next.focus();
        });
    });
}

/**
 * Data tables: click a header to sort by that column.
 *
 * Rows carry no explicit order, so the original sequence is captured up front to
 * make a third click restore it.
 */
function initSortableTable(table) {
    const body = table.querySelector('tbody');
    const headers = Array.from(table.querySelectorAll('thead th'));

    if (!body || headers.length < 2) {
        return;
    }

    const original = Array.from(body.rows);
    const cellText = (row, index) => (row.cells[index] ? row.cells[index].textContent.trim() : '');

    headers.forEach((header, index) => {
        header.setAttribute('role', 'button');
        header.setAttribute('tabindex', '0');

        const apply = () => {
            const current = header.getAttribute('aria-sort');
            const next = current === 'ascending' ? 'descending' : (current === 'descending' ? 'none' : 'ascending');

            headers.forEach((other) => other.removeAttribute('aria-sort'));

            if (next === 'none') {
                original.forEach((row) => body.appendChild(row));
                return;
            }

            header.setAttribute('aria-sort', next);

            const direction = next === 'ascending' ? 1 : -1;
            Array.from(body.rows)
                .sort((a, b) => direction * cellText(a, index).localeCompare(cellText(b, index)))
                .forEach((row) => body.appendChild(row));
        };

        header.addEventListener('click', apply);
        header.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                apply();
            }
        });
    });
}

function init() {
    document.querySelectorAll('.som-map--interactive').forEach(initMap);
    document.querySelectorAll('[data-legality-country-select]').forEach(initCountrySelect);
    document.querySelectorAll('[data-legality-tabs]').forEach(initTabs);
    document.querySelectorAll('.som-table[data-sortable]').forEach(initSortableTable);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
